

import { useEffect, useRef, useState } from "react";
import { ContentView } from "../../components/content-view/ContentView";
import ContextualAppBar from "../../components/contextual-app-bar/ContextualAppBar";
import { ViewOptionStateEnum } from "../../components/contextual-app-bar/ViewOptionStateEnum";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { IOrganization } from "../../services/organization-data-service/Models";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useGroup } from "../../queries/groups/UseGroup";
import { useOrganizations } from "../../queries/organizations/UseOrganizations";
import { actionItems } from "./AlarmSchemeAdminActions";
import { ActionMenuItem, BaseActions } from "../../components/action-menu/ActionMenuItem";
import AlarmSchemeList, { AlarmSchemeListType } from "../../components/alarm-scheme-list/AlarmSchemeList";
import { useForm } from "react-hook-form";
import { AlarmSchemeListActionEnum, AlarmSchemesFormType } from "../../components/alarm-scheme-list/AlarmSchemeListUtils";
import { useGetAlarmSchemes } from "../../queries/alarm-scheme-admin/UseGetAlarmSchemes";
import { useAlarmSchemeReferenceData } from "../../queries/alarm-scheme-admin/UseAlarmSchemeReferenceData";
import AlarmSchemeAdministrationUtils from "./AlarmSchemeAdministrationUtils";
import LoadingZone from "../../components/loading-zone/LoadingZone";
import Form from "../../components/forms/form/Form";
import { ContentContainer } from "../../components/content-container/ContentContainer";
import { ContentSection } from "../../components/content-section/ContentSection";
import OptionsSideSheet from "../../components/options-side-sheet/OptionsSideSheet";
import SideSheetFilterInput from "../../components/side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import { useTranslation } from "react-i18next";

export default function AlarmSchemeAdministrationView() {
    const [userState] = useUserContext();
    const routeContext = useRouteContext();
    const [organizationID, setOrganizationID] = useState<number>();
    const [filterText, setFilterText] = useState('');

    const { t } = useTranslation();
    const alarmSchemeListRef = useRef<AlarmSchemeListType>(undefined);

    const {data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number
    });

    const { data: organizations } = useQueryNotifications(useOrganizations)({
        includeAllOption: true
    });

    const organization = organizations?.find(o => o.id === organizationID);

    const {data, status, refetch } = useQueryNotifications(useGetAlarmSchemes)({
        organizationID: organization?.id as number,
        groupID: organization?.peerGroupID as number,
        enabled: organization !== undefined
    });


    const {data: referenceData, status: referenceStatus, refetch: refetchReferenceData} = useQueryNotifications(useAlarmSchemeReferenceData)({
        organizationID: organization?.id as number,
        groupID: organization?.peerGroupID as number,
        enabled: organization !== undefined
    });

    const viewOptionState = filterText === '' ? ViewOptionStateEnum.Default : ViewOptionStateEnum.Changed;

    const form = useForm<AlarmSchemesFormType>({
        //mode: "all",
        mode: 'onChange',
        defaultValues: {
            alarmSchemes: []
        }
    });

    const {
        reset,
    } = form;

    function onExecuteAction(menuItem: ActionMenuItem) {

         const item = menuItem.action as AlarmSchemeListActionEnum | BaseActions;

        switch (item) {
            case AlarmSchemeListActionEnum.Add:
                alarmSchemeListRef?.current?.executeAction(item);
                break;
        }
    }

    function onOrganizationChanged(item: IOrganization) {
        setOrganizationID(item.id);
    }

    async function onDataChanged() {

        await Promise.all([refetch(), refetchReferenceData()]);

    }

    useEffect(() => {
        if (group && organizationID === undefined) {
            setOrganizationID(group.organizationID as number);
        }
    }, [
        group,
        organizationID,
        setOrganizationID
    ]);

    useEffect(() => {

        if (data && referenceData) {
            reset(AlarmSchemeAdministrationUtils.mapToFormValues(data, referenceData));
        }
    }, [
        data,
        referenceData,
        reset,
    ]);


    return (
        <ContentView>
            <ContextualAppBar 
                hideBreadcrumb={true}
                actions={actionItems} 
                viewOptionState={viewOptionState}
                onExecuteAction={onExecuteAction} 
                showOrganizationPicker={true}
                onOrganizationSelected={onOrganizationChanged}
                organizationID={organizationID}
            />

                <LoadingZone isLoading={status === 'loading' || referenceStatus === 'loading'}>

                <ContentContainer>
                    <ContentSection>
                        {
                            // use a form here so the AlarmSchemeList component can be used.  Not doing any form editing here
                            organization && data && referenceData &&
                            <Form form={form} sx={{ padding: 0 }}>

                                <AlarmSchemeList
                                    ref={alarmSchemeListRef}
                                    groupID={organization.peerGroupID} 
                                    organizationID={organizationID as number}
                                    viewSelected={false}
                                    filterText={filterText}
                                    enableSelection={false}
                                    allowEdit={true}
                                    showAvatar={true}
                                    showSetpoints={true}
                                    onDataChanged={() => onDataChanged()}
                                />

                            </Form>
                        }
                    </ContentSection>

                    <OptionsSideSheet onReset={() => setFilterText('')}>
                    
                        <SideSheetFilterInput
                            label={t("setpointAdmin.filterBy")}
                            value={filterText}
                            onChange={(value: string) => {
                                setFilterText(value.trim());
                            }}
                        />


                    </OptionsSideSheet>

                </ContentContainer>                    
                    

            </LoadingZone>
        </ContentView>
    );
  }
